import type { NotificationExpandedDTO } from "@/api/vtnz-backend/notifications";

/**
 * PLACEHOLDER pending backend confirmation - see project memory
 * "notification-related-item-gap". `notifications.type`
 * ([notifications.ts](../../api/vtnz-backend/generated/models/notifications.ts))
 * is a plain `text` column with no DB-level enum, so there's nothing to
 * introspect from the schema - these values are a guess at what the
 * backend will actually send, not a confirmed contract. Boyd is asking the
 * backend team for the real list; update this (and
 * `resolveNotificationTarget` below) once they confirm it.
 */
export type NotificationSubjectType =
  | "vehicle"
  | "vehicle_group"
  | "location"
  | "member";

/**
 * Where a notification should navigate to when clicked, expressed in terms
 * of backend/external ids - resolving those to whatever local id a
 * particular domain store keys its own navigation on (e.g. Vehicle/Trip's
 * local `.id` vs `.externalId`) is the caller's job, not this function's.
 */
export type NotificationNavigationTarget =
  | { kind: "trip"; tripExternalId: string }
  | { kind: "vehicle"; vehicleExternalId: string }
  | { kind: "vehicle-group"; vehicleExternalIds: string[] }
  | { kind: "location"; locationId: string }
  | { kind: "member"; userId: string }
  | { kind: "none" };

/**
 * `associated_id` can carry one id or several (comma-separated) for a
 * batched notification, e.g. "3 vehicles overdue for RUC" - PLACEHOLDER
 * encoding pending backend confirmation of how multi-subject notifications
 * are actually represented.
 */
function parseAssociatedIds(associatedId: string | null): string[] {
  if (!associatedId) return [];
  return associatedId
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

/**
 * Single source of truth for "what does clicking this notification do".
 * `related_route_plan_id` is a dedicated, already-confirmed field so it's
 * checked first; everything else falls back to the generic `type`/
 * `associated_id` pair (PLACEHOLDER mapping, see NotificationSubjectType
 * above). Returns `{ kind: "none" }` for anything unrecognised so an
 * older/unmapped notification just doesn't navigate rather than navigating
 * somewhere wrong.
 */
export function resolveNotificationTarget(
  notification: NotificationExpandedDTO,
): NotificationNavigationTarget {
  if (notification.related_route_plan_id) {
    return { kind: "trip", tripExternalId: notification.related_route_plan_id };
  }

  const [firstId, ...ids] = parseAssociatedIds(notification.associated_id);
  if (!firstId) return { kind: "none" };

  switch (notification.type as NotificationSubjectType | null) {
    case "vehicle":
      return { kind: "vehicle", vehicleExternalId: firstId };
    case "vehicle_group":
      return { kind: "vehicle-group", vehicleExternalIds: [firstId, ...ids] };
    case "location":
      return { kind: "location", locationId: firstId };
    case "member":
      return { kind: "member", userId: firstId };
    default:
      return { kind: "none" };
  }
}
